import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

interface Activity {
  id: string;
  user_id: string;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  details: Record<string, unknown> | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

interface ActivityResponse {
  success: boolean;
  data: {
    activities: Activity[];
    total: number;
    page: number;
    limit: number;
  };
}

const API_URL = '';

const actionLabels: Record<string, string> = {
  register: 'Регистрация',
  login: 'Вход',
  logout: 'Выход',
  password_change: 'Смена пароля',
  profile_update: 'Обновление профиля',
  payment_succeeded: 'Успешный платёж',
  subscription_cancelled: 'Отмена подписки',
  subscription_tier_changed: 'Изменение тарифа',
  subscription_extended: 'Продление подписки',
  forecast_request: 'Запрос прогноза',
  calculation_natal: 'Расчёт натальной карты',
  calculation_numerology: 'Расчёт нумерологии',
  synastry_request: 'Расчёт совместимости',
  contact_add: 'Добавление контакта',
  export_pdf: 'Экспорт PDF',
};

const AdminUserActivityPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [actionFilter, setActionFilter] = useState('');
  const [userName, setUserName] = useState('');

  const loadActivities = async () => {
    if (!id) return;

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50',
        ...(actionFilter && { action: actionFilter }),
      });

      const res = await fetch(`/api/admin/users/${id}/activity?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const json: ActivityResponse = await res.json();

      if (json.success) {
        setActivities(json.data.activities);
        setTotal(json.data.total);
      } else {
        setError(json.message || 'Ошибка загрузки');
      }
    } catch (err: any) {
      setError(err.message || 'Ошибка соединения');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadActivities();
  }, [id, page, actionFilter]);

  const uniqueActions = Array.from(new Set(activities.map(a => a.action)));

  if (loading && activities.length === 0) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#d4af37' }}>Загрузка...</div>;
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: '8px 16px',
            borderRadius: '8px',
            border: '1px solid #444',
            background: 'transparent',
            color: '#aaa',
            cursor: 'pointer',
          }}
        >
          ← Назад
        </button>
        <h1 style={{ color: '#d4af37', fontSize: '28px', margin: 0 }}>
          Логи активности пользователя
        </h1>
      </div>

      {/* Filter */}
      <div style={{ marginBottom: '24px' }}>
        <select
          value={actionFilter}
          onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
          style={{
            padding: '10px 16px',
            borderRadius: '8px',
            border: '1px solid #444',
            background: 'rgba(13, 25, 48, 0.8)',
            color: '#fff',
            fontSize: '14px',
          }}
        >
          <option value="">Все действия</option>
          {uniqueActions.map(action => (
            <option key={action} value={action}>{actionLabels[action] || action}</option>
          ))}
        </select>
      </div>

      {error && (
        <div style={{
          background: 'rgba(220, 53, 69, 0.2)',
          border: '1px solid #dc3545',
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '16px',
          color: '#dc3545',
        }}>
          {error}
        </div>
      )}

      {/* Timeline */}
      <div style={{ position: 'relative', paddingLeft: '32px' }}>
        <div style={{
          position: 'absolute',
          left: '12px',
          top: 0,
          bottom: 0,
          width: '2px',
          background: 'rgba(212, 175, 55, 0.3)',
        }} />

        {activities.map((activity) => (
          <div
            key={activity.id}
            style={{
              position: 'relative',
              marginBottom: '16px',
              padding: '16px',
              background: 'rgba(13, 25, 48, 0.6)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '12px',
            }}
          >
            {/* Dot */}
            <div style={{
              position: 'absolute',
              left: '-26px',
              top: '20px',
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              background: '#d4af37',
              border: '2px solid #0a0a1a',
            }} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <div>
                <h4 style={{ color: '#d4af37', margin: '0 0 8px', fontSize: '16px' }}>
                  {actionLabels[activity.action] || activity.action}
                </h4>

                {activity.resource_type && (
                  <p style={{ color: '#aaa', margin: '0 0 8px', fontSize: '14px' }}>
                    Ресурс: {activity.resource_type}
                    {activity.resource_id && ` (${activity.resource_id.slice(0, 8)}...)`}
                  </p>
                )}

                {activity.details && (
                  <pre style={{
                    background: 'rgba(0,0,0,0.3)',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: '#888',
                    overflow: 'auto',
                    maxWidth: '600px',
                  }}>
                    {JSON.stringify(activity.details, null, 2)}
                  </pre>
                )}
              </div>

              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <p style={{ color: '#888', fontSize: '13px', margin: '0 0 4px' }}>
                  {new Date(activity.created_at).toLocaleDateString('ru-RU')}
                </p>
                <p style={{ color: '#666', fontSize: '12px', margin: 0 }}>
                  {new Date(activity.created_at).toLocaleTimeString('ru-RU')}
                </p>
                {activity.ip_address && (
                  <p style={{ color: '#666', fontSize: '11px', margin: '4px 0 0', fontFamily: 'monospace' }}>
                    {activity.ip_address}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {total > 50 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '24px' }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid #444',
              background: page === 1 ? 'transparent' : 'rgba(13, 25, 48, 0.8)',
              color: page === 1 ? '#666' : '#fff',
              cursor: page === 1 ? 'not-allowed' : 'pointer',
            }}
          >
            Назад
          </button>
          <span style={{ color: '#aaa', padding: '8px' }}>
            Страница {page} из {Math.ceil(total / 50)}
          </span>
          <button
            onClick={() => setPage(p => p + 1)}
            disabled={page >= Math.ceil(total / 50)}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid #444',
              background: page >= Math.ceil(total / 50) ? 'transparent' : 'rgba(13, 25, 48, 0.8)',
              color: page >= Math.ceil(total / 50) ? '#666' : '#fff',
              cursor: page >= Math.ceil(total / 50) ? 'not-allowed' : 'pointer',
            }}
          >
            Вперёд
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminUserActivityPage;
